import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { fetchSessions, itemLabel, saveResult, type Item, type Session } from './api';
import { addDays, today } from './dates';
import './extras.css';

export default function NowNextPage() {
  const t = useT();
  const { sessionId } = useParams();
  const [search] = useSearchParams();
  const { learnerId: selected } = useLearner();
  const learnerId = search.get('learner') ?? selected;
  const [session, setSession] = useState<Session | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passed, setPassed] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const nowRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (!learnerId || !sessionId) return;
    const day = today();
    fetchSessions(learnerId, addDays(day, -60), addDays(day, 60))
      .then((list) => {
        const found = list.find((s) => s.id === sessionId) ?? null;
        setSession(found);
        setMissing(!found);
      })
      .catch((e) => setError(e instanceof Error ? e.message : t('The session could not be loaded.')));
  }, [learnerId, sessionId, t]);

  const left: Item[] = session ? session.items.filter((i) => i.status === 'PLANNED' && !passed.includes(i.id)) : [];
  const now = left[0] ?? null;
  const next = left[1] ?? null;

  useEffect(() => { nowRef.current?.focus(); }, [now?.id]);

  async function finish() {
    if (!now || !learnerId) return;
    setBusy(true);
    setError(null);
    try {
      await saveResult(learnerId, now.id, 'DONE', now.result);
      setPassed((l) => [...l, now.id]);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="planner-now">
      <Link className="planner-now-back" to="/plan">{t('Back to the plan')}</Link>
      {!learnerId && <p className="planner-now-message">{t('Choose a learner first.')}</p>}
      {error && <p className="planner-now-message" role="alert">{error}</p>}
      {learnerId && !session && !missing && !error && <p className="planner-now-message" role="status">{t('Loading…')}</p>}
      {missing && <p className="planner-now-message">{t('This session could not be found.')}</p>}
      {session && now && (
        <div className="planner-now-stage">
          <section className="planner-now-card is-now" aria-labelledby="planner-now-title">
            <p className="planner-now-label">{t('Now')}</p>
            <h1 id="planner-now-title" className="planner-now-name" tabIndex={-1} ref={nowRef}>{itemLabel(now)}</h1>
            {now.materials && <p className="planner-now-detail">{now.materials}</p>}
            {now.minutes != null && <p className="planner-now-detail">{t('{n} minutes', { n: now.minutes })}</p>}
            <div className="planner-now-actions">
              {now.kind === 'PROGRAM' && now.programId && (
                <Link className="btn secondary planner-now-button" to={`/play/${now.programId}?learner=${encodeURIComponent(learnerId ?? '')}`}>{t('Play')}</Link>
              )}
              <button type="button" className="btn planner-now-button" disabled={busy} onClick={() => void finish()}>{t('Finished')}</button>
            </div>
          </section>
          {next && (
            <section className="planner-now-card is-next" aria-labelledby="planner-next-title">
              <p className="planner-now-label">{t('Next')}</p>
              <h2 id="planner-next-title" className="planner-now-name">{itemLabel(next)}</h2>
            </section>
          )}
        </div>
      )}
      {session && !now && (
        <div className="planner-now-stage">
          <section className="planner-now-card is-done" aria-labelledby="planner-done-title">
            <svg className="planner-now-tick" viewBox="0 0 48 48" aria-hidden="true">
              <circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" strokeWidth="3" />
              <path d="M14 25l7 7 13-15" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h1 id="planner-done-title" className="planner-now-name">{t('All done')}</h1>
          </section>
        </div>
      )}
    </div>
  );
}
